import { useState, useMemo } from "react";
import Teacherheader from "./Teacherheader";
import Teachersidebar from "./Teachersidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line, Cell  } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Users, Download, Search, 
  Target, 
  ArrowUpRight, ArrowDownRight, Printer,
  BarChart3, Trophy, AlertCircle, CheckCircle2, AlertTriangle
} from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { SidebarProvider } from "@/components/ui/sidebar";

import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

// Mock student data generator
const generateMockStudents = () => {
  const firstNames = ["John", "Emma", "Liam", "Olivia", "Noah", "Ava", "Ethan", "Sophia", "Mason", "Isabella", 
    "Lucas", "Mia", "Oliver", "Charlotte", "Aiden", "Amelia", "Elijah", "Harper", "James", "Evelyn",
    "Benjamin", "Abigail", "William", "Emily", "Henry", "Elizabeth", "Sebastian", "Sofia", "Jack", "Avery"];
  const lastNames = ["Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Rodriguez", "Martinez",
    "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas", "Taylor", "Moore", "Jackson", "Martin",
    "Lee", "Perez", "Thompson", "White", "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson"];

  const subjects = ["Math", "English", "Science", "History", "Geography", "Art"];
  
  return Array.from({ length: 30 }, (_, i) => {
    const firstName = firstNames[i];
    const lastName = lastNames[i];
    const grades: Record<string, { score: number; grade: string; proficiency: string }> = {};
    
    subjects.forEach(subject => {
      const score = Math.floor(Math.random() * 40) + 60; // 60-100 range
      grades[subject] = {
        score,
        grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F',
        proficiency: score >= 90 ? 'Advanced' : score >= 80 ? 'Proficient' : score >= 70 ? 'Developing' : 'Beginning'
      };
    });

    const avgScore = Math.round(Object.values(grades).reduce((sum, g) => sum + g.score, 0) / subjects.length);
    
    return {
      id: i + 1,
      name: `${firstName} ${lastName}`,
      studentId: `STU${String(i + 1).padStart(4, '0')}`,
      grades,
      overallScore: avgScore,
      overallGrade: avgScore >= 90 ? 'A' : avgScore >= 80 ? 'B' : avgScore >= 70 ? 'C' : avgScore >= 60 ? 'D' : 'F',
      rank: 0,
      trend: Math.random() > 0.3 ? 'up' : 'down',
      trendValue: Math.floor(Math.random() * 10) + 1,
      notes: '',
      assignments: [
        { name: 'Midterm Exam', score: Math.floor(Math.random() * 30) + 70, maxScore: 100, date: '2025-10-15' },
        { name: 'Quiz 1', score: Math.floor(Math.random() * 20) + 80, maxScore: 100, date: '2025-09-20' },
        { name: 'Project', score: Math.floor(Math.random() * 25) + 75, maxScore: 100, date: '2025-11-01' },
        { name: 'Homework Avg', score: Math.floor(Math.random() * 15) + 85, maxScore: 100, date: '2025-11-15' },
      ],
      termProgress: [
        { term: 'Term 1', score: Math.floor(Math.random() * 20) + 65 },
        { term: 'Term 2', score: Math.floor(Math.random() * 15) + 70 },
        { term: 'Term 3', score: avgScore },
      ]
    };
  }).sort((a, b) => b.overallScore - a.overallScore).map((student, index) => ({
    ...student,
    rank: index + 1
  }));
};

const mockClasses = [
  { id: '10A', name: 'Grade 10A', students: 30 },
  { id: '10B', name: 'Grade 10B', students: 28 },
  { id: '11A', name: 'Grade 11A', students: 25 },
];

const mockTerms = [
  { id: 'term3-2025', name: 'Term 3 2025' },
  { id: 'term2-2025', name: 'Term 2 2025' },
  { id: 'term1-2025', name: 'Term 1 2025' },
];

const subjects = ["Math", "English", "Science", "History", "Geography", "Art"];


const GRADE_COLORS = {
  A: 'hsl(142, 76%, 36%)',
  B: 'hsl(173, 58%, 39%)',
  C: 'hsl(48, 96%, 53%)',
  D: 'hsl(25, 95%, 53%)',
  F: 'hsl(0, 84%, 60%)'
};

const standards = [
  { id: 1, name: 'Problem Solving', subject: 'Math' },
  { id: 2, name: 'Number Operations', subject: 'Math' },
  { id: 3, name: 'Reading Comprehension', subject: 'English' },
  { id: 4, name: 'Writing Skills', subject: 'English' },
  { id: 5, name: 'Scientific Method', subject: 'Science' },
  { id: 6, name: 'Lab Skills', subject: 'Science' },
  { id: 7, name: 'Historical Analysis', subject: 'History' },
  { id: 8, name: 'Map Reading', subject: 'Geography' },
];

const TeacherAcademicDashboard = () => {
  const [selectedClass, setSelectedClass] = useState('10A');
  const [selectedTerm, setSelectedTerm] = useState('term3-2025');
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'rank' | 'score'>('rank');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedStudent, setSelectedStudent] = useState<any>(null);

  const [studentNotes, setStudentNotes] = useState<Record<number, string>>({});

  const students = useMemo(() => generateMockStudents(), [selectedClass, selectedTerm]);

  // Calculate statistics
  const stats = useMemo(() => {
    const scores = students.map(s => s.overallScore);
    const passCount = students.filter(s => s.overallScore >= 70).length;
    return {
      classAverage: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      totalStudents: students.length,
      passRate: Math.round((passCount / students.length) * 100),
      highestScore: Math.max(...scores),
      lowestScore: Math.min(...scores),
      topPerformer: students[0],
      lowestPerformer: students[students.length - 1],
      atRiskCount: students.filter(s => s.overallScore < 70).length,
      medianScore: scores.sort((a, b) => a - b)[Math.floor(scores.length / 2)]
    };
  }, [students]);

  // Grade distribution
  const gradeDistribution = useMemo(() => {
    const distribution = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    students.forEach(s => {
      distribution[s.overallGrade as keyof typeof distribution]++;
    });
    return Object.entries(distribution).map(([grade, count]) => ({
      grade,
      count,
      percentage: Math.round((count / students.length) * 100),
      fill: GRADE_COLORS[grade as keyof typeof GRADE_COLORS]
    }));
  }, [students]);

  // Subject averages
  const subjectAverages = useMemo(() => {
    return subjects.map(subject => {
      const avg = Math.round(
        students.reduce((sum, s) => sum + s.grades[subject].score, 0) / students.length
      );
      return {
        subject,
        average: avg,
        benchmark: Math.floor(Math.random() * 10) + 70, // Mock benchmark
        fill: avg >= 80 ? 'hsl(142, 76%, 36%)' : avg >= 70 ? 'hsl(48, 96%, 53%)' : 'hsl(0, 84%, 60%)'
      };
    });
  }, [students]);



  // Filtered and sorted students
  const filteredStudents = useMemo(() => {
    let result = students.filter(s => 
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.studentId.toLowerCase().includes(searchQuery.toLowerCase())
    );

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'rank':
          comparison = a.rank - b.rank;
          break;
        case 'score':
          comparison = b.overallScore - a.overallScore;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [students, searchQuery, sortBy, sortOrder]);

  // Standards mastery data
  const standardsMastery = useMemo(() => {
    return standards.map(standard => {
      const levels = ['Beginning', 'Developing', 'Proficient', 'Advanced'];
      const distribution = levels.map(level => ({
        level,
        count: Math.floor(Math.random() * 10) + 2
      }));
      return {
        ...standard,
        distribution,
        classAvg: ['Beginning', 'Developing', 'Proficient', 'Advanced'][Math.floor(Math.random() * 4)]
      };
    });
  }, []);




  const handleExportCSV = () => {
    const headers = ['Rank', 'Name', 'ID', 'Overall %', 'Grade', ...subjects];
    const rows = filteredStudents.map(s => [
      s.rank,
      s.name,
      s.studentId,
      s.overallScore,
      s.overallGrade,
      ...subjects.map(sub => s.grades[sub].score)
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_${selectedClass}_results.csv`;
    a.click();
  };



  const chartConfig = {
    average: { label: "Class Average", color: "hsl(var(--chart-1))" },
    benchmark: { label: "Benchmark", color: "hsl(var(--chart-2))" },
  };



  const getMasteryColor = (level: string) => {
    switch (level) {
      case 'Advanced': return 'bg-green-500';
      case 'Proficient': return 'bg-blue-500';
      case 'Developing': return 'bg-yellow-500';
      case 'Beginning': return 'bg-red-500';
      default: return 'bg-muted';
    }
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <div className="h-full border-r dark:border-gray-700">
          <Teachersidebar />
        </div>
        <div className="flex flex-col flex-1 h-full">
          <Teacherheader />
          <main className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-950">
            <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Teacher Academic Dashboard</h1>
          <p className="text-muted-foreground">View and analyze class academic results</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Select class" />
            </SelectTrigger>
            <SelectContent>
              {mockClasses.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Select term" />
            </SelectTrigger>
            <SelectContent>
              {mockTerms.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1">
          <TabsTrigger value="overview" className="flex-1 min-w-[100px]">Overview</TabsTrigger>
          <TabsTrigger value="students" className="flex-1 min-w-[100px]">Students List</TabsTrigger>
          <TabsTrigger value="class-compare" className="flex-1 min-w-[120px]">Class Comparisons</TabsTrigger>
          <TabsTrigger value="subject-compare" className="flex-1 min-w-[130px]">Subject Comparisons</TabsTrigger>
          <TabsTrigger value="standards" className="flex-1 min-w-[130px]">Standards Mastery</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Class Average</p>
                    <p className="text-2xl font-bold">{stats.classAverage}%</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Students</p>
                    <p className="text-2xl font-bold">{stats.totalStudents}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pass Rate</p>
                    <p className="text-2xl font-bold">{stats.passRate}%</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">At Risk</p>
                    <p className="text-2xl font-bold text-destructive">{stats.atRiskCount}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top/Bottom Performers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  Top Performer
                </CardTitle>
	           </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-lg font-bold">
                    {stats.topPerformer?.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold">{stats.topPerformer?.name}</p>
                    <p className="text-sm text-muted-foreground">{stats.highestScore}% - Grade {stats.topPerformer?.overallGrade}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  Needs Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-lg font-bold">
                    {stats.lowestPerformer?.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold">{stats.lowestPerformer?.name}</p>
                    <p className="text-sm text-muted-foreground">{stats.lowestScore}% - Grade {stats.lowestPerformer?.overallGrade}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Grade Distribution Heatmap */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Grade Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {gradeDistribution.map(item => (
                  <div key={item.grade} className="text-center p-4 rounded-lg" style={{ backgroundColor: item.fill + '30' }}>
                    <p className="text-2xl font-bold" style={{ color: item.fill }}>{item.grade}</p>
                    <p className="text-lg font-semibold">{item.count}</p>
                    <p className="text-xs text-muted-foreground">{item.percentage}%</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Students List Tab */}
        <TabsContent value="students" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Student Performance List</CardTitle>
              <CardDescription>Detailed academic performance for all students in {selectedClass}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between">
                <div className="flex gap-2 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search students..."
                      className="pl-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rank">Rank</SelectItem>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="score">Score</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                    {sortOrder === 'asc' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                  </Button>
                </div>
                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Rank</TableHead>
                      <TableHead>Student Name</TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead className="text-right">Overall Score</TableHead>
                      <TableHead className="text-center">Grade</TableHead>
                      <TableHead>Trend</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudents.map((student) => (
                      <TableRow key={student.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedStudent(student)}>
                        <TableCell className="font-medium">
                          <div className={`flex items-center justify-center h-8 w-8 rounded-full ${student.rank <= 3 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-100' : 'bg-muted'}`}>
                            {student.rank}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{student.name}</p>
                            <p className="text-xs text-muted-foreground">{student.studentId}</p>
                          </div>
                        </TableCell>
                        <TableCell>{student.studentId}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-bold ${student.overallScore < 70 ? 'text-destructive' : ''}`}>
                            {student.overallScore}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`${
                            student.overallGrade === 'A' ? 'border-green-500 text-green-500' :
                            student.overallGrade === 'F' ? 'border-destructive text-destructive' : ''
                          }`}>
                            {student.overallGrade}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {student.trend === 'up' ? (
                              <ArrowUpRight className="h-4 w-4 text-green-500" />
                            ) : (
                              <ArrowDownRight className="h-4 w-4 text-destructive" />
                            )}
                            <span className="text-xs text-muted-foreground">{student.trendValue}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStudent(student);
                          }}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Class Comparison Tab */}
        <TabsContent value="class-compare" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Class Performance Comparison</CardTitle>
                <CardDescription>Average scores across different classes</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { name: '10A', score: 78, fill: 'hsl(var(--chart-1))' },
                      { name: '10B', score: 74, fill: 'hsl(var(--chart-2))' },
                      { name: '11A', score: 82, fill: 'hsl(var(--chart-3))' },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 100]} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="score" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pass Rate Comparison</CardTitle>
                <CardDescription>Percentage of students passing by class</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { name: '10A', rate: 85, fill: 'hsl(142, 76%, 36%)' },
                      { name: '10B', rate: 78, fill: 'hsl(48, 96%, 53%)' },
                      { name: '11A', rate: 92, fill: 'hsl(142, 76%, 36%)' },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 100]} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="rate" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subject Comparisons Tab */}
        <TabsContent value="subject-compare" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Subject Performance Comparison</CardTitle>
              <CardDescription>Average scores across all subjects</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={subjectAverages} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="subject" width={100} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="average" radius={[0, 4, 4, 0]}>
                      {subjectAverages.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Subject Performance Heatmap Grid */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Class Performance Heatmap</CardTitle>
              <CardDescription>Color-coded performance grid (Green = High, Red = Low)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="grid gap-1" style={{ gridTemplateColumns: `auto repeat(${subjects.length}, 1fr)` }}>
                  <div className="p-2 font-semibold text-sm">Student</div>
                  {subjects.map(sub => (
                    <div key={sub} className="p-2 font-semibold text-sm text-center">{sub}</div>
                  ))}
                  
                  {students.slice(0, 15).map(student => (
                    <>
                      <div key={`name-${student.id}`} className="p-2 text-sm truncate">{student.name}</div>
                      {subjects.map(sub => {
                        const score = student.grades[sub].score;
                        const bgColor = score >= 90 ? 'bg-green-500' : score >= 80 ? 'bg-green-400' : score >= 70 ? 'bg-yellow-400' : score >= 60 ? 'bg-orange-400' : 'bg-red-500';
                        return (
                          <div 
                            key={`${student.id}-${sub}`} 
                            className={`p-2 text-center text-xs font-medium ${bgColor} text-white rounded`}
                          >
                            {score}
                          </div>
                        );
                      })}
                    </>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4 text-center">Showing top 15 students. View full list in Students tab.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Standards Mastery Tab */}
        <TabsContent value="standards" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-5 w-5" />
                Standards & Skills Mastery
              </CardTitle>
              <CardDescription>Proficiency levels across key learning standards</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {standardsMastery.map(standard => (
                  <div key={standard.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{standard.name}</p>
                        <p className="text-xs text-muted-foreground">{standard.subject}</p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={getMasteryColor(standard.classAvg) + ' text-white border-0'}
                      >
                        {standard.classAvg}
                      </Badge>
                    </div>
                    <div className="flex gap-1 h-6">
                      {standard.distribution.map((d, i) => (
                        <TooltipProvider key={i}>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className={`${getMasteryColor(d.level)} rounded cursor-pointer transition-all hover:opacity-80`}
                                style={{ flex: d.count }}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{d.level}: {d.count} students</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mt-6 justify-center flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded" />
                  <span className="text-xs">Advanced</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded" />
                  <span className="text-xs">Proficient</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded" />
                  <span className="text-xs">Developing</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded" />
                  <span className="text-xs">Beginning</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Class Gaps */}
          <Card className="border-yellow-200 dark:border-yellow-800">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                Areas Needing Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {standardsMastery.filter(s => s.classAvg === 'Beginning' || s.classAvg === 'Developing').slice(0, 4).map(standard => (
                  <div key={standard.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${getMasteryColor(standard.classAvg)}`} />
                    <div>
                      <p className="font-medium text-sm">{standard.name}</p>
                      <p className="text-xs text-muted-foreground">{standard.subject} - {standard.classAvg}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Student Detail Modal */}
      <Dialog open={!!selectedStudent} onOpenChange={() => setSelectedStudent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedStudent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
                    {selectedStudent.name.charAt(0)}
                  </div>
                  <div>
                    <p>{selectedStudent.name}</p>
                    <p className="text-sm font-normal text-muted-foreground">{selectedStudent.studentId} • Rank #{selectedStudent.rank}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Overall Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{selectedStudent.overallScore}%</p>
                    <p className="text-xs text-muted-foreground">Overall</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold" style={{ color: GRADE_COLORS[selectedStudent.overallGrade as keyof typeof GRADE_COLORS] }}>
                      {selectedStudent.overallGrade}
                    </p>
                    <p className="text-xs text-muted-foreground">Grade</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold flex items-center justify-center">
                      {selectedStudent.trend === 'up' ? (
                        <span className="text-green-600">+{selectedStudent.trendValue}%</span>
                      ) : (
                        <span className="text-red-600">-{selectedStudent.trendValue}%</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">Trend</p>
                  </div>
                </div>

                {/* Subject Breakdown */}
                <div>
                  <h4 className="font-semibold mb-3">Subject Breakdown</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {subjects.map(sub => (
                      <div key={sub} className="p-3 bg-muted rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium">{sub}</span>
                          <Badge variant="outline" className="text-xs">
                            {selectedStudent.grades[sub].grade}
                          </Badge>
                        </div>
                        <p className="text-lg font-bold">{selectedStudent.grades[sub].score}%</p>
                        <p className="text-xs text-muted-foreground">{selectedStudent.grades[sub].proficiency}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assignment Breakdown */}
                <div>
                  <h4 className="font-semibold mb-3">Recent Assignments</h4>
                  <div className="space-y-2">
                    {selectedStudent.assignments.map((assignment: any, i: number) => (
                      <div key={i} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                        <div>
                          <p className="text-sm font-medium">{assignment.name}</p>
                          <p className="text-xs text-muted-foreground">{assignment.date}</p>
                        </div>
                        <span className="font-semibold">{assignment.score}/{assignment.maxScore}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Progress Chart */}
                <div>
                  <h4 className="font-semibold mb-3">Term Progress</h4>
                  <ChartContainer config={chartConfig} className="h-[150px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={selectedStudent.termProgress}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="term" />
                        <YAxis domain={[50, 100]} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line type="monotone" dataKey="score" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>

                {/* Notes */}
                <div>
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <Textarea 
                    placeholder="Add notes about this student..."
                    value={studentNotes[selectedStudent.id] || ''}
                    onChange={(e) => setStudentNotes(prev => ({ ...prev, [selectedStudent.id]: e.target.value }))}
                    className="min-h-[80px]"
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => {
                    const csvContent = `Student Report\n${selectedStudent.name}\n${selectedStudent.studentId}\n\nOverall: ${selectedStudent.overallScore}%\nGrade: ${selectedStudent.overallGrade}\n\nSubjects:\n${subjects.map(s => `${s}: ${selectedStudent.grades[s].score}%`).join('\n')}`;
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedStudent.name.replace(' ', '_')}_report.txt`;
                    a.click();
                  }}>
                    <Download className="h-4 w-4 mr-2" />
                    Export Report
                  </Button>
                  <Button variant="outline" onClick={() => window.print()}>
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default TeacherAcademicDashboard;